import { resolveUploadImageUrl } from 'api/utils/resolveUploadImageUrl'
import {
  AMENITY_KEYS,
  CreateAdFlowForm,
  SERVICE_KEYS,
} from 'components/createAd/createAdFlowState'
import { capTransferIncentive } from 'components/createAd/createAdValidation'
import ListingPhotoGalleryModal from 'components/createAd/ListingPhotoGalleryModal'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  formatLocalizedDate,
  moneyFormatLocale,
  normalizeLangCode,
  tListingTypeLabel,
  tRoomCountLabel,
  tUnitTypeLabel,
} from 'utils/i18nLocale'
import { formatMoneyDisplay } from 'utils/moneyInput'

export type PublicationPublishChecks = {
  accurate: boolean
  visible: boolean
  terms: boolean
}

type CreateAdPublicationReviewProps = {
  form: CreateAdFlowForm
  publishChecks: PublicationPublishChecks
  onPublishChecksChange: (checks: PublicationPublishChecks) => void
  publishChecksError?: boolean
}

const DESCRIPTION_PREVIEW_LENGTH = 320

function FeaturePill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700">
      {children}
    </span>
  )
}

function PhotoTile({
  src,
  onClick,
  className,
  children,
}: {
  src: string
  onClick: () => void
  className?: string
  children?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative block w-full overflow-hidden rounded-xl bg-gray-100 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-imovPrimary ${className ?? ''}`}
    >
      <img src={src} alt="" className="h-full w-full object-cover" />
      {children}
    </button>
  )
}

const CreateAdPublicationReview = ({
  form,
  publishChecks,
  onPublishChecksChange,
  publishChecksError = false,
}: CreateAdPublicationReviewProps) => {
  const { t, i18n } = useTranslation()
  const lang = normalizeLangCode(i18n.language)
  const moneyLocale = moneyFormatLocale(lang)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [galleryIndex, setGalleryIndex] = useState(0)

  const photoUrls = useMemo(
    () =>
      form.listingPhotos
        .map(photo => resolveUploadImageUrl(photo.url))
        .filter(url => url !== ''),
    [form.listingPhotos]
  )

  const openGallery = (startIndex: number) => {
    setGalleryIndex(startIndex)
    setGalleryOpen(true)
  }

  const amenitiesOn = AMENITY_KEYS.filter(key => form.amenities[key])

  const includesLine = useMemo(() => {
    const labels = SERVICE_KEYS.filter(key => key !== 'electromenagers')
      .filter(key => form.services[key])
      .map(key => t(`createAd.flow.services.${key}`))
    if (form.electromenagersMode === 'included') {
      labels.push(t('createAd.flow.electromenagers.includedLabel'))
    } else if (form.electromenagersMode === 'paid') {
      labels.push(t('createAd.flow.electromenagers.paidLabel'))
    }
    return labels.join(', ')
  }, [form.electromenagersMode, form.services, t])

  const amenityLabels = useMemo(() => {
    const labels = amenitiesOn.map(key => t(`createAd.flow.amenities.${key}` as const))
    if (form.laundry === 'in_unit') {
      labels.unshift(t('createAd.flow.publication.preview.laundryInUnit'))
    } else if (form.laundry === 'in_building') {
      labels.unshift(t('createAd.flow.publication.preview.laundryInBuilding'))
    }
    return labels
  }, [amenitiesOn, form.laundry, t])

  const locationLine = [form.neighborhood.trim(), form.city.trim()].filter(Boolean).join(', ')
  const priceFormatted = formatMoneyDisplay(form.monthlyRent, moneyLocale)
  const title = form.listingTitle.trim() || t('createAd.flow.publication.preview.untitled')
  const description = form.description.trim()
  const descriptionNeedsTruncate = description.length > DESCRIPTION_PREVIEW_LENGTH
  const descriptionPreview =
    descriptionNeedsTruncate && !descriptionExpanded
      ? `${description.slice(0, DESCRIPTION_PREVIEW_LENGTH).trim()}…`
      : description

  const incentiveFormatted = useMemo(() => {
    const capped = capTransferIncentive(form.transferIncentive)
    if (!capped) return ''
    return formatMoneyDisplay(capped, moneyLocale)
  }, [form.transferIncentive, moneyLocale])

  const petsPill =
    form.pets === 'no'
      ? t('createAd.flow.publication.preview.petsNo')
      : form.pets === 'yes' || form.pets === 'conditions'
        ? form.petsConditionsNote.trim()
          ? t('createAd.flow.publication.preview.petsConditions')
          : t('createAd.flow.publication.preview.petsYes')
        : null

  const roomsPill = form.roomCount
    ? form.roomCount === 'studio'
      ? tRoomCountLabel(t, 'studio')
      : `${tRoomCountLabel(t, form.roomCount)} ${t('createAd.flow.publication.preview.roomsSuffix')}`
    : null

  const bathsPill = form.bathrooms
    ? `${form.bathrooms} ${t('createAd.flow.publication.preview.bathsSuffix')}`
    : null

  const unitPill = form.unitType ? tUnitTypeLabel(t, form.unitType) : null

  const listingTypeLabel = form.listingType
    ? tListingTypeLabel(t, form.listingType)
    : null

  const setCheck = (key: keyof PublicationPublishChecks, value: boolean) => {
    onPublishChecksChange({ ...publishChecks, [key]: value })
  }

  const visibleAmenities = amenityLabels.slice(0, 3)
  const hiddenAmenityCount = Math.max(0, amenityLabels.length - 3)

  const checkRowClass = (checked: boolean) =>
    publishChecksError && !checked
      ? 'rounded-xl border border-red-300 bg-red-50/80 p-2 -m-2'
      : undefined

  return (
    <>
      <ListingPhotoGalleryModal
        photos={photoUrls}
        initialIndex={galleryIndex}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
      />

      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-lg font-bold text-imovSecondary">
              <svg
                className="h-5 w-5 text-imovPrimary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {t('createAd.flow.publication.preview.title')}
            </h2>
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
              {t('createAd.flow.publication.preview.publicBadge')}
            </span>
          </div>

          <article className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            {photoUrls.length > 0 ? (
              <div className="p-2">
                <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_120px] md:grid-cols-[minmax(0,1fr)_140px] sm:grid-rows-2">
                  <PhotoTile
                    src={photoUrls[0]}
                    onClick={() => openGallery(0)}
                    className="min-h-[200px] sm:col-span-1 sm:row-span-2 sm:min-h-[260px]"
                  >
                    {listingTypeLabel ? (
                      <span className="absolute left-3 top-3 rounded-md bg-imovPrimary px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow">
                        {listingTypeLabel}
                      </span>
                    ) : null}
                  </PhotoTile>

                  {photoUrls[1] ? (
                    <PhotoTile
                      src={photoUrls[1]}
                      onClick={() => openGallery(1)}
                      className="hidden min-h-[124px] sm:block"
                    />
                  ) : (
                    <div className="hidden sm:block" aria-hidden />
                  )}

                  {photoUrls[2] ? (
                    <PhotoTile
                      src={photoUrls[2]}
                      onClick={() => openGallery(photoUrls.length > 3 ? 0 : 2)}
                      className="hidden min-h-[124px] sm:block"
                    >
                      {photoUrls.length > 3 ? (
                        <span className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 text-center text-white">
                          <span className="text-[10px] font-bold uppercase tracking-wider">
                            {t('createAd.flow.publication.preview.viewAll')}
                          </span>
                          <span className="text-sm font-semibold">
                            {photoUrls.length} {t('createAd.flow.publication.preview.photos')}
                          </span>
                        </span>
                      ) : null}
                    </PhotoTile>
                  ) : photoUrls.length > 1 ? (
                    <div className="hidden sm:block" aria-hidden />
                  ) : null}
                </div>

                {photoUrls.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => openGallery(0)}
                    className="mt-2 w-full rounded-xl border border-gray-200 py-2 text-center text-sm font-semibold text-imovPrimary sm:hidden"
                  >
                    {t('createAd.flow.publication.preview.viewAll')} ({photoUrls.length})
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="flex aspect-[16/9] items-center justify-center bg-gray-100 text-sm text-gray-400">
                {t('createAd.flow.publication.review.noPhotos')}
              </div>
            )}

            <div className="space-y-4 border-t border-gray-100 p-5 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="break-words text-xl font-bold leading-snug text-gray-900 sm:text-2xl">
                    {title}
                  </h3>
                  {locationLine ? (
                    <p className="mt-2 flex items-center gap-1.5 text-sm text-gray-600">
                      <svg
                        className="h-4 w-4 shrink-0 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      {locationLine}
                    </p>
                  ) : null}
                  {form.availabilityDate.trim() ? (
                    <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-imovPrimary">
                      <svg
                        className="h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      {t('createAd.flow.publication.preview.availableFrom', {
                        date: formatLocalizedDate(form.availabilityDate, lang),
                      })}
                    </p>
                  ) : null}
                </div>
                <div className="shrink-0 text-left sm:text-right">
                  <p className="break-words text-2xl font-bold text-gray-900 sm:text-3xl">
                    {priceFormatted ? `${priceFormatted} $` : '—'}
                    {priceFormatted ? (
                      <span className="text-base font-semibold text-gray-500">
                        {' '}
                        {t('createAd.flow.suffix.cadPerMonth')}
                      </span>
                    ) : null}
                  </p>
                  {includesLine ? (
                    <p className="mt-1 break-words text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:max-w-xs sm:ml-auto">
                      {t('createAd.flow.publication.preview.included')}: {includesLine}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {unitPill ? <FeaturePill>{unitPill}</FeaturePill> : null}
                {roomsPill ? <FeaturePill>{roomsPill}</FeaturePill> : null}
                {bathsPill ? <FeaturePill>{bathsPill}</FeaturePill> : null}
                {form.virtualTourLink.trim() ? (
                  <FeaturePill>{t('createAd.flow.publication.preview.virtualTour')}</FeaturePill>
                ) : null}
                {petsPill ? <FeaturePill>{petsPill}</FeaturePill> : null}
              </div>

              <div className="grid gap-6 border-t border-gray-100 pt-5 md:grid-cols-2">
                <div className="min-w-0">
                  <h4 className="mb-2 flex items-center gap-2 text-sm font-bold text-imovSecondary">
                    <svg
                      className="h-4 w-4 text-imovPrimary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    {t('createAd.flow.publication.preview.about')}
                  </h4>
                  {description ? (
                    <>
                      <p className="break-words whitespace-pre-wrap text-sm leading-relaxed text-gray-700 [overflow-wrap:anywhere]">
                        {descriptionPreview}
                      </p>
                      {descriptionNeedsTruncate ? (
                        <button
                          type="button"
                          onClick={() => setDescriptionExpanded(v => !v)}
                          className="mt-2 text-sm font-semibold text-imovPrimary hover:text-imovPrimaryDark"
                        >
                          {descriptionExpanded
                            ? t('createAd.flow.publication.preview.readLess')
                            : t('createAd.flow.publication.preview.readMore')}
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {t('createAd.flow.publication.review.noDescription')}
                    </p>
                  )}
                </div>
                <div className="min-w-0">
                  <h4 className="mb-2 flex items-center gap-2 text-sm font-bold text-imovSecondary">
                    <svg
                      className="h-4 w-4 text-imovPrimary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {t('createAd.flow.publication.preview.amenities')}
                  </h4>
                  {amenityLabels.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {visibleAmenities.map(label => (
                        <FeaturePill key={label}>{label}</FeaturePill>
                      ))}
                      {hiddenAmenityCount > 0 ? (
                        <FeaturePill>
                          {t('createAd.flow.publication.preview.moreAmenities', {
                            count: hiddenAmenityCount,
                          })}
                        </FeaturePill>
                      ) : null}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {t('createAd.flow.publication.preview.noAmenities')}
                    </p>
                  )}
                </div>
              </div>

              {incentiveFormatted ? (
                <div className="flex min-w-0 items-start gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
                  <span className="shrink-0 text-lg" aria-hidden>
                    📣
                  </span>
                  <p className="min-w-0 break-words text-sm text-green-900 [overflow-wrap:anywhere]">
                    <span className="font-bold uppercase tracking-wide">
                      {t('createAd.flow.publication.preview.incentiveLabel')}:{' '}
                    </span>
                    {t('createAd.flow.publication.preview.incentiveBody', {
                      amount: incentiveFormatted,
                    })}
                  </p>
                </div>
              ) : null}
            </div>
          </article>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-imovSecondary">
              <svg
                className="h-4 w-4 text-imovPrimary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {t('createAd.flow.publication.preview.beforePublish')}
            </h3>
            {publishChecksError ? (
              <p
                role="alert"
                className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
              >
                {t('createAd.flow.publication.preview.publishChecksError')}
              </p>
            ) : null}
            <ul className="space-y-3">
              <li className={checkRowClass(publishChecks.accurate)}>
                <label className="flex cursor-pointer gap-3">
                  <input
                    type="checkbox"
                    checked={publishChecks.accurate}
                    onChange={e => setCheck('accurate', e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-imovPrimary focus:ring-imovPrimary"
                  />
                  <span className="text-sm leading-relaxed text-gray-700">
                    {t('createAd.flow.publication.preview.checkAccurate')}
                  </span>
                </label>
              </li>
              <li className={checkRowClass(publishChecks.visible)}>
                <label className="flex cursor-pointer gap-3">
                  <input
                    type="checkbox"
                    checked={publishChecks.visible}
                    onChange={e => setCheck('visible', e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-imovPrimary focus:ring-imovPrimary"
                  />
                  <span className="text-sm leading-relaxed text-gray-700">
                    {t('createAd.flow.publication.preview.checkVisible')}
                  </span>
                </label>
              </li>
              <li className={checkRowClass(publishChecks.terms)}>
                <label className="flex cursor-pointer gap-3">
                  <input
                    type="checkbox"
                    checked={publishChecks.terms}
                    onChange={e => setCheck('terms', e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-imovPrimary focus:ring-imovPrimary"
                  />
                  <span className="text-sm leading-relaxed text-gray-700">
                    {t('createAd.flow.publication.preview.checkTermsPrefix')}{' '}
                    <Link to="/terms-of-use" className="font-semibold text-imovPrimary hover:underline">
                      {t('createAd.flow.publication.preview.termsLink')}
                    </Link>{' '}
                    {t('createAd.flow.publication.preview.checkTermsAnd')}{' '}
                    <Link to="/terms-of-use" className="font-semibold text-imovPrimary hover:underline">
                      {t('createAd.flow.publication.preview.privacyLink')}
                    </Link>
                    .
                  </span>
                </label>
              </li>
            </ul>
          </section>
        </div>

        <aside className="w-full shrink-0 space-y-4 lg:w-[280px] lg:sticky lg:top-24">
          <div className="rounded-2xl bg-imovSecondary p-5 text-white shadow-md">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-imovPrimary/30 text-xl">
              🚀
            </div>
            <h3 className="mb-3 text-sm font-bold">
              {t('createAd.flow.publication.preview.afterPublishTitle')}
            </h3>
            <ul className="space-y-2 text-xs leading-relaxed text-white/90">
              <li className="flex gap-2">
                <span className="text-imovPrimary">✓</span>
                {t('createAd.flow.publication.preview.afterPublish1')}
              </li>
              <li className="flex gap-2">
                <span className="text-imovPrimary">✓</span>
                {t('createAd.flow.publication.preview.afterPublish2')}
              </li>
              <li className="flex gap-2">
                <span className="text-imovPrimary">✓</span>
                {t('createAd.flow.publication.preview.afterPublish3')}
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
            <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-imovSecondary">
              <svg
                className="h-4 w-4 text-imovPrimary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {t('createAd.flow.publication.preview.helpTitle')}
            </h3>
            <p className="text-xs leading-relaxed text-gray-600">
              {t('createAd.flow.publication.preview.helpBody')}
            </p>
          </div>
        </aside>
      </div>
    </>
  )
}

export default CreateAdPublicationReview
